"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { useRouter, useParams } from "next/navigation"

type Props = {
  /**
   * Locale resolved on the server (from `headers().get("x-locale")` or
   * `params.countryCode`). Passed in from server components so the
   * placeholder/labels render the same on SSR and client — avoids hydration
   * mismatches when `useParams()` returns nothing during SSR.
   */
  locale?: string
  /**
   * If true, the search box is rendered for mobile usage (full-width, no
   * `hidden lg:block` gate). Defaults to false (desktop visibility only).
   */
  mobile?: boolean
}

type SearchHit = {
  id: string
  title: string
  handle: string
  sku: string
  /** UAH, whole-hryvnia (not cents) — written by the Meili indexer */
  price_uah: number
  manufacturer?: string
  thumbnail?: string
  in_stock: boolean
  seller_slug?: string
  category_names?: string[]
  _formatted?: { title?: string }
}

type SearchResult = {
  hits: SearchHit[]
  query: string
  totalHits: number
  processingTimeMs: number
}

const placeholders: Record<string, string> = {
  ua: "Пошук: транзистор, резистор 10k, блок живлення 12V...",
  en: "Search: transistor, resistor 10k, power supply 12V...",
  ru: "Поиск: транзистор, резистор 10к, блок питания 12В...",
}

// TODO(i18n): migrate to ui.ts keys
//   search.results / search.pcs / search.all / search.empty / search.time
//   search.in_stock / search.out_of_stock
// Suggested copies below; do not lock them in until i18n review.
const labels: Record<string, Record<string, string>> = {
  ua: { results: "результатів", pcs: "шт.", all: "Всі результати", empty: "Нічого не знайдено", time: "мс", inStock: "В наявності", outOfStock: "Немає" },
  en: { results: "results", pcs: "pcs", all: "All results", empty: "Nothing found", time: "ms", inStock: "In stock", outOfStock: "Out" },
  ru: { results: "результатов", pcs: "шт.", all: "Все результаты", empty: "Ничего не найдено", time: "мс", inStock: "В наличии", outOfStock: "Нет" },
}

function getLocale(cc: string) {
  if (cc === "ru") return "ru"
  if (["us", "gb", "en"].includes(cc)) return "en"
  return "ua"
}

function formatPrice(uah: number) {
  if (!uah) return ""
  return `${uah.toLocaleString("uk-UA")} ₴`
}

export default function SearchBox({ locale: localeProp, mobile = false }: Props = {}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const router = useRouter()
  const params = useParams()

  // Prefer server-provided locale (stable across SSR + hydration). Fall back
  // to client-side useParams() for legacy callers that don't pass it in.
  const locale = getLocale(localeProp || (params?.countryCode as string) || "ua")
  const l = labels[locale] || labels.ua

  // Search with debounce
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults(null)
      setIsOpen(false)
      return
    }

    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}&limit=8`)
      const data: SearchResult = await res.json()
      setResults(data)
      setIsOpen(true)
      setActiveIndex(-1)
    } catch {
      setResults(null)
    }
  }, [])

  const handleChange = (value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    // 300ms debounce — matches the global app's typing rhythm and keeps the
    // /api/search proxy from being hit on every keystroke.
    debounceRef.current = setTimeout(() => doSearch(value), 300)
  }

  // Navigate to product. Marketplace canonical URL is
  // /{cc}/catalog/s-{sellerSlug}/{productHandle}; fall back to the legacy
  // /{cc}/products/{handle} route when the indexed doc has no seller_slug
  // (legacy radar-online products imported before the seller stamping pass).
  const goToProduct = (handle: string, sellerSlug?: string) => {
    setIsOpen(false)
    setQuery("")
    const cc = params?.countryCode || "ua"
    const url = sellerSlug
      ? `/${cc}/catalog/s-${sellerSlug}/${handle}`
      : `/${cc}/products/${handle}`
    router.push(url)
  }

  // Navigate to search results page
  const goToAll = () => {
    setIsOpen(false)
    router.push(`/${params?.countryCode || "ua"}/store?q=${encodeURIComponent(query)}`)
  }

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!results?.hits.length) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, results.hits.length))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, -1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < results.hits.length) {
        const hit = results.hits[activeIndex]
        goToProduct(hit.handle, hit.seller_slug)
      } else {
        goToAll()
      }
    } else if (e.key === "Escape") {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div className={mobile ? "relative w-full" : "relative hidden flex-[1.4] lg:block"}>
      {/* Input */}
      <div
        className={`flex items-center rounded-full border border-slate-200 bg-white/80 shadow-sm ring-1 ring-black/5 transition focus-within:border-cyan-300 focus-within:ring-cyan-300/30 dark:border-white/10 dark:bg-white/5 dark:ring-white/5 dark:focus-within:border-cyan-300/60 ${
          mobile ? "px-4 py-2" : "px-5 py-3"
        }`}
      >
        <svg
          className="mr-3 h-4 w-4 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results?.hits.length && setIsOpen(true)}
          className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 dark:text-white"
          placeholder={placeholders[locale] || placeholders.ua}
        />
        {query && (
          <button
            onClick={() => {
              setQuery("")
              setResults(null)
              setIsOpen(false)
              inputRef.current?.focus()
            }}
            className="ml-2 text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && results && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-[#0a1628]"
        >
          {results.hits.length > 0 ? (
            <>
              {/* Stats */}
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 text-xs text-slate-400 dark:border-white/10">
                <span>
                  {results.totalHits} {l.results}
                </span>
                <span>{results.processingTimeMs} {l.time}</span>
              </div>

              {/* Hits */}
              <div className="max-h-[400px] overflow-y-auto">
                {results.hits.map((hit, i) => (
                  <button
                    key={hit.id}
                    onClick={() => goToProduct(hit.handle, hit.seller_slug)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition ${
                      i === activeIndex
                        ? "bg-cyan-50 dark:bg-cyan-400/10"
                        : "hover:bg-slate-50 dark:hover:bg-white/5"
                    }`}
                  >
                    {/* Thumbnail */}
                    {hit.thumbnail ? (
                      <img
                        src={hit.thumbnail}
                        alt=""
                        className="h-10 w-10 rounded-lg border border-slate-100 bg-white object-contain p-1 dark:border-white/10 dark:bg-white/5"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-100 bg-slate-50 text-slate-300 dark:border-white/10 dark:bg-white/5">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="h-5 w-5">
                          <path d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3l3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                        </svg>
                      </div>
                    )}

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900 dark:text-white">
                        {hit.title}
                      </div>
                      <div className="flex items-center gap-2 truncate text-xs text-slate-400">
                        {hit.sku && <span>#{hit.sku}</span>}
                        {hit.manufacturer && (
                          <>
                            <span>·</span>
                            <span>{hit.manufacturer}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Stock + Price */}
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        {formatPrice(hit.price_uah)}
                      </span>
                      {hit.in_stock ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          {l.inStock}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                          {l.outOfStock}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* All results link */}
              {results.totalHits > results.hits.length && (
                <button
                  onClick={goToAll}
                  onMouseEnter={() => setActiveIndex(results.hits.length)}
                  className={`flex w-full items-center justify-center border-t border-slate-100 px-4 py-3 text-sm font-medium transition dark:border-white/10 ${
                    activeIndex === results.hits.length
                      ? "bg-cyan-50 text-cyan-700 dark:bg-cyan-400/10 dark:text-cyan-400"
                      : "text-cyan-600 hover:bg-slate-50 dark:text-cyan-400 dark:hover:bg-white/5"
                  }`}
                >
                  {l.all} ({results.totalHits}) →
                </button>
              )}
            </>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              {l.empty}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
